import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ServiceAreas from '@/components/ServiceAreas';
import SEO from '@/components/SEO';
import EpicHeroBackground from '@/components/EpicHeroBackground';
import '@/styles/heroAnimations.css';
import { 
  Sparkles, MapPin, Clock, Star, Shield, CheckCircle, 
  ArrowRight, Car, Droplets, Zap, Phone, MessageCircle 
} from 'lucide-react';
import ReactCompareImage from 'react-compare-image';

const HomePage = () => {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const services = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Paint Correction',
      description: 'Professional multi-step paint correction to restore your vehicle\'s shine',
    },
    {
      icon: <Car className="w-8 h-8" />,
      title: 'Ceramic Coating',
      description: 'Long-lasting protection with premium ceramic coating technology',
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: 'Interior Detailing',
      description: 'Deep cleaning and restoration of your vehicle\'s interior',
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Headlight Restoration',
      description: 'Crystal-clear headlights for improved visibility and appearance',
    },
  ];

  const benefits = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Mobile Service',
      description: 'We come to your location in Brooklyn and nearby areas',
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: 'Premium Quality',
      description: 'Professional tools and materials for exceptional results',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Fast Response',
      description: 'Quick scheduling and efficient service delivery',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Personalized Approach',
      description: 'Custom pricing and service plans for every vehicle',
    },
  ];

  const testimonials = [
    {
      name: 'Michael Thompson',
      rating: 5,
      text: 'ONYXA transformed my car! The paint correction made it look brand new. Highly recommend their mobile service!',
      vehicle: '2020 BMW 3 Series',
    },
    {
      name: 'Sarah Martinez',
      rating: 5,
      text: 'Professional, punctual, and the results are amazing. My headlights are crystal clear now!',
      vehicle: '2018 Honda Accord',
    },
    {
      name: 'David Lee',
      rating: 5,
      text: 'Best detailing service in Brooklyn! The ceramic coating is worth every penny. My car looks incredible.',
      vehicle: '2021 Tesla Model 3',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen bg-black">
      <SEO 
        title="ONYXA Car Polishing & Detailing – Premium Mobile Detailing"
        description="Professional paint correction, ceramic coating, and mobile detailing services throughout the NYC & Tri-State area."
        canonicalPath="/"
      />
      <Navigation />

      {/* Epic Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Epic Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black"></div>
        <EpicHeroBackground />
        
        {/* Ambient light */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black opacity-60"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-32">
          {/* Logo with epic animation */}
          <div data-testid="hero-logo" className="mb-12 flex justify-center epic-hero-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_mobile-detailing-11/artifacts/n9c26bxp_Untitled%20design.png" 
              alt="ONYXA Car Polishing & Detailing" 
              className="h-72 w-auto transform hover:scale-105 transition-transform duration-700"
            />
          </div>

          {/* Epic Title with letter spacing animation */}
          <h1 
            data-testid="hero-title"
            className="epic-hero-title text-6xl sm:text-7xl lg:text-8xl mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            <span className="block mb-4">P R E M I U M</span>
            <span className="block shimmer-text text-7xl sm:text-8xl lg:text-9xl">M O B I L E</span>
            <span className="block mt-4">D E T A I L I N G</span>
          </h1>

          {/* Subtitle with fade animation */}
          <p data-testid="hero-subtitle" className="epic-hero-subtitle text-base sm:text-lg text-gray-400 mb-12 max-w-3xl mx-auto uppercase tracking-wider">
            E x p e r t &nbsp; P a i n t &nbsp; C o r r e c t i o n &nbsp; • &nbsp; C e r a m i c &nbsp; C o a t i n g &nbsp; • &nbsp; M o b i l e &nbsp; S e r v i c e
          </p>

          {/* Epic CTA buttons */}
          <div data-testid="hero-cta-buttons" className="epic-hero-cta flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button
              data-testid="hero-book-appointment-btn"
              onClick={() => navigate('/booking')}
              className="epic-button premium-glow px-10 py-5 bg-gradient-to-r from-red-600 via-red-700 to-red-600 text-white rounded-none font-semibold text-lg tracking-wider uppercase hover:from-red-700 hover:via-red-800 hover:to-red-700 transition-all w-full sm:w-auto flex items-center justify-center space-x-3"
            >
              <span>Book Now</span>
              <ArrowRight size={24} />
            </button>
            <button
              data-testid="hero-request-quote-btn"
              onClick={() => navigate('/contact')}
              className="epic-button px-10 py-5 border-2 border-white/20 text-white rounded-none font-semibold text-lg tracking-wider uppercase hover:border-red-500 hover:bg-red-500/10 transition-all w-full sm:w-auto"
            >
              Request Quote
            </button>
          </div>

          {/* Contact buttons - modern minimal */}
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center opacity-80 hover:opacity-100 transition-opacity">
            <a
              href="tel:9297746344"
              data-testid="hero-call-btn"
              className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
            >
              <Phone size={18} />
              <span className="text-sm tracking-wider">929-774-6344</span>
            </a>
            <span className="hidden sm:block text-gray-600">|</span>
            <a
              href="sms:9297746344"
              data-testid="hero-text-btn"
              className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
            >
              <MessageCircle size={18} />
              <span className="text-sm tracking-wider">Text Us</span>
            </a>
          </div>

          {/* Trust indicators */}
          <div data-testid="trust-indicators" className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <CheckCircle className="text-red-500" size={20} />
              <span>Mobile Service</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="text-red-500" size={20} />
              <span>Premium Products</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="text-red-500" size={20} />
              <span>Expert Technicians</span>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Section */}
      <section data-testid="before-after-section" className="py-20 px-6 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              data-testid="before-after-title"
              className="text-4xl lg:text-5xl font-bold mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Before & <span className="text-red-500">After</span>
            </h2>
            <p data-testid="before-after-subtitle" className="text-lg text-gray-300 max-w-3xl mx-auto">
              See the transformation our professional detailing services deliver
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Example 1 - Paint Correction */}
            <div className="glass-card p-6 hover:scale-[1.02] transition-transform">
              <div className="mb-4">
                <ReactCompareImage
                  leftImage="https://images.unsplash.com/photo-1708805282706-f44730b7e527?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxwYWludCUyMGNvcnJlY3Rpb258ZW58MHx8fHwxNzY0NDQ2Njk5fDA&ixlib=rb-4.1.0&q=85"
                  rightImage="https://images.unsplash.com/photo-1708805282695-ef186db20192?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxwYWludCUyMGNvcnJlY3Rpb258ZW58MHx8fHwxNzY0NDQ2Njk5fDA&ixlib=rb-4.1.0&q=85"
                  sliderLineColor="#E52424"
                  sliderLineWidth={4}
                />
              </div>
              <h3 className="text-xl font-semibold mb-2">Paint Correction & Polish</h3>
              <p className="text-sm text-gray-400">Professional multi-step paint correction removing swirl marks and scratches</p>
            </div>

            {/* Example 2 - Wheel Detailing */}
            <div className="glass-card p-6 hover:scale-[1.02] transition-transform">
              <div className="mb-4">
                <ReactCompareImage
                  leftImage="https://images.unsplash.com/photo-1708805282683-50a060eba80f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxhdXRvJTIwZGV0YWlsaW5nfGVufDB8fHx8MTc2NDQ0NjcxMnww&ixlib=rb-4.1.0&q=85"
                  rightImage="https://images.unsplash.com/photo-1708805283017-c662be2c7a44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxhdXRvJTIwZGV0YWlsaW5nfGVufDB8fHx8MTc2NDQ0NjcxMnww&ixlib=rb-4.1.0&q=85"
                  sliderLineColor="#E52424"
                  sliderLineWidth={4}
                />
              </div>
              <h3 className="text-xl font-semibold mb-2">Premium Wheel Detailing</h3>
              <p className="text-sm text-gray-400">Deep cleaning and restoration of wheels, tires, and brake components</p>
            </div>
          </div>

          {/* View Full Gallery Button */}
          <div className="text-center mt-12">
            <button
              data-testid="view-gallery-btn"
              onClick={() => navigate('/gallery')}
              className="px-8 py-4 bg-gradient-to-r from-red-600 to-red-800 text-white rounded-full font-semibold hover:from-red-700 hover:to-red-900 transition-all inline-flex items-center gap-2"
            >
              View Full Gallery
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              data-testid="services-title"
              className="text-4xl lg:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Our Services
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Professional detailing services tailored to your vehicle's needs
            </p>
          </div>

          <div data-testid="services-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="glass-card p-6 hover:scale-105 transition-transform duration-300 group cursor-pointer"
                onClick={() => navigate('/services')}
              >
                <div className="text-red-500 mb-4 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-gray-400 text-sm">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              data-testid="view-all-services-btn"
              onClick={() => navigate('/services')}
              className="btn-secondary"
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section data-testid="benefits-section" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              data-testid="benefits-title"
              className="text-4xl lg:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Why Choose <span className="text-red-500">ONYXA</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Experience the difference with our premium mobile detailing service
            </p>
          </div>

          <div data-testid="benefits-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} data-testid={`benefit-item-${index}`} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-600 to-red-800 rounded-2xl mb-4">
                  <div className="text-white">{benefit.icon}</div>
                </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              data-testid="testimonials-title"
              className="text-4xl lg:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Client Reviews
            </h2>
            <p className="text-gray-400">What our satisfied customers have to say</p>
          </div>

          <div data-testid="testimonial-card" className="glass-card p-8 md:p-12 max-w-3xl mx-auto">
            <div className="flex justify-center mb-4">
              {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                <Star key={i} className="text-red-500 fill-red-500" size={24} />
              ))}
            </div>
            <p className="text-lg md:text-xl text-gray-300 mb-6 italic text-center">
              "{testimonials[currentTestimonial].text}"
            </p>
            <div className="text-center">
              <p className="font-semibold text-white">{testimonials[currentTestimonial].name}</p>
              <p className="text-sm text-gray-400">{testimonials[currentTestimonial].vehicle}</p>
            </div>
          </div>

          {/* Testimonial dots */}
          <div data-testid="testimonial-dots" className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                data-testid={`testimonial-dot-${index}`}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentTestimonial ? 'bg-red-500 w-8' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <ServiceAreas />

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center glass-card p-12 rounded-3xl">
          <h2 
            data-testid="cta-title"
            className="text-4xl lg:text-5xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Ready to Transform Your Vehicle?
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Book your appointment today, request a custom quote, or contact us directly
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <button
              data-testid="cta-book-now-btn"
              onClick={() => navigate('/booking')}
              className="btn-primary"
            >
              Book Now
            </button>
            <button
              data-testid="cta-get-quote-btn"
              onClick={() => navigate('/contact')}
              className="btn-secondary"
            >
              Get a Quote
            </button>
            <a
              href="tel:9297746344"
              data-testid="cta-call-btn"
              className="btn-primary flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
              style={{ boxShadow: '0 4px 20px rgba(34, 197, 94, 0.3)' }}
            >
              <Phone size={20} />
              <span>Call Now</span>
            </a>
            <a
              href="sms:9297746344"
              data-testid="cta-text-btn"
              className="btn-secondary border-blue-500 text-blue-400 hover:bg-blue-500/10 flex items-center justify-center"
            >
              <MessageCircle size={20} className="mr-2" />
              <span>Text Us</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
